import { get } from 'lodash';

import { Controller } from 'react-hook-form';

import { FormItem, NumberInput } from '@ingrid/luterkort-react';

import { SimpleSelect } from '~src/components/SimpleSelect/SimpleSelect';

import { ValidateSchema } from '../../create/validate';
import { DynamicPath, useCarrierContractFormContext } from '../../types';
import { useUISchema } from '../hooks';
import { Schema } from '../queries';
import { SchemaFormLabel } from './schema-form-label';
import { SchemaFormSubText } from './schema-form-sub-text';

interface Props {
  defaultValue?: number;
  disabled?: boolean;
  schemaId: string;
  schema: Schema;
  path?: string;
}

function SchemaFormIntegerOneOf({ schema, schemaId, disabled, defaultValue, path = '' }: Props) {
  const { formState, control } = useCarrierContractFormContext();

  if (schema.type !== 'integer') {
    return null;
  }

  return (
    <Controller
      rules={ValidateSchema(path, schemaId, schema)}
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
      defaultValue={defaultValue as any}
      name={path as DynamicPath}
      control={control}
      render={({ field }) => (
        <SimpleSelect
          options={(schema.oneOf ?? []).map(({ const: value, title }) => [value.toString(), title])}
          onValueChange={([value]) => field.onChange(+value)}
          value={[String(field.value as unknown as string)]}
          invalid={!!get(formState.errors, path)?.message}
          disabled={disabled}
          ref={field.ref}
        />
      )}
    />
  );
}

function SchemaFormIntegerNumber({ schemaId, disabled, schema, defaultValue, path = '' }: Props) {
  const { control } = useCarrierContractFormContext();

  return (
    <Controller
      rules={ValidateSchema(path, schemaId, schema)}
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
      defaultValue={defaultValue as any}
      name={path as DynamicPath}
      control={control}
      render={({ field, fieldState }) => (
        <FormItem.Control invalid={fieldState.invalid}>
          <NumberInput
            {...field}
            onChange={event => field.onChange(event)}
            value={field.value as number | undefined}
            invalid={fieldState.invalid}
            disabled={disabled}
          />
        </FormItem.Control>
      )}
    />
  );
}

export function SchemaFormInteger(props: Props) {
  const { defaultValue } = useUISchema({ schemaId: props.schemaId, path: props.path ?? '' });

  if (props.schema.type !== 'integer') {
    return null;
  }

  const Component = props.schema.oneOf ? SchemaFormIntegerOneOf : SchemaFormIntegerNumber;

  return (
    <FormItem.Root>
      <SchemaFormLabel schema={props.schema} path={props.path ?? ''} schemaId={props.schemaId} />
      <Component {...props} defaultValue={defaultValue as number | undefined} />
      <SchemaFormSubText schema={props.schema} path={props.path} />
    </FormItem.Root>
  );
}
